import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import { Alert, AlertColor, Box, Button, ButtonGroup, Card, Divider, Skeleton, Stack } from "@mui/material";
import React, { useContext, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePage } from "../../routes";
import { useSwitchgearDelete } from "../data/Assets/useSwitchgearDelete";
import { useSwitchgearsBySubstation } from "../data/Assets/useSwitchgearsBySubstation";
import { useSwitchgearUpdate } from "../data/Assets/useSwitchgearUpdate";
import { CancelButton } from "../shared/CancelButton";
import { HVSubstationSetupErrorCode } from "../shared/enums/ErrorCodes";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";

import { Asset } from "../shared/interfaces/Asset";

import { HvSubstationErrorResponse } from "../shared/interfaces/HvSubstation";
import { mapSwitchgearFromAsset } from "../shared/interfaces/Switchgear";
import { MessageCard, MessageType } from "../shared/MessageCard";
import { ProgressButton } from "../shared/ProgressButton";
import { Snackbar } from "../shared/Snackbar";
import { TabView } from "../shared/TabView";
import { WorkflowViewer } from "../workflows/WorkflowViewer";

import { AccessControlContext } from "../contexts/AccessControlContext";
import { useAddSurveyableAssetToHvSubstation } from "../data/Assets/useAddSurveyableAssetToHvSubstation";
import { getAssetTypeLabel, managedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { ResultSurveyableAsset } from "../shared/interfaces/Result";
import {
  ExistingSurveyableAsset,
  SurveyableAsset,
  SurveyableAssetSubmission,
} from "../shared/interfaces/SurveyableAsset";
import { SwitchgearCreateDialog } from "./SwitchgearCreateDialog";
import SwitchgearForm from "./SwitchgearForm";
import { SwitchgearLabel } from "./SwitchgearLabel";
import { SwitchgearMetadata } from "./SwitchgearMetadata";
import { UpdatablePanelDetails } from "./UpdatablePanelDetails";

interface Props {
  substationId: number;
  editable?: boolean;
  customerId?: number;
}

export function UpdatableSwitchgearDetails(props: Props) {
  const { t } = useTranslation();
  const accessControlContext = useContext(AccessControlContext);
  const userCanDeleteSwitchgears = accessControlContext.canI("delete", "switchgears", props.customerId);
  const userCanEditSwitchgears = accessControlContext.canI("edit", "switchgears", props.customerId);
  const workflowViewerPath = usePage(WorkflowViewer.name)?.path;

  const { substationId, editable } = props;

  const switchgearsBySubstation = useSwitchgearsBySubstation(substationId, {
    onSuccess: onFetchSwitchgearsBySubstationSuccess,
  });
  const switchgearAdd = useAddSurveyableAssetToHvSubstation({
    onSuccess: onAddedSwitchgearSuccess,
  });
  const switchgearDelete = useSwitchgearDelete({
    onSuccess: onDeletedSwitchgearSuccess,
    onError: onDeleteSwitchgearError,
  });
  const switchgearUpdate = useSwitchgearUpdate({
    onSuccess: onUpdateSwitchgearSuccess,
    onError: onUpdateSwitchgearError,
  });

  const [switchgears, setSwitchgears] = useState<ExistingSurveyableAsset[]>([]);
  const [editing, setEditing] = useState(false);

  const [affectedWorkflowIds, setAffectedWorkflowIds] = useState<number[]>([]);

  const [switchgearDialogVisible, setSwitchgearDialogVisible] = useState<boolean>(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (selectedTabIndex: number) => {
    setActiveTab(selectedTabIndex);
  };

  function onFetchSwitchgearsBySubstationSuccess(response: Asset[]) {
    if (!response) return;

    if (activeTab > response.length - 1) setActiveTab(0);

    setSwitchgears(response.map<ExistingSurveyableAsset>(mapSwitchgearFromAsset));
  }

  function onAddedSwitchgearSuccess() {
    setSnackbarText(t("asset.assetCreated"));
    setSnackbarSeverity("success");
    setShowSnackbar(true);

    switchgearsBySubstation.refetch().then((response) => {
      const indexOfLastSwitchgear = response.data ? response.data.length - 1 : 0;
      setActiveTab(indexOfLastSwitchgear);
    });
  }

  function onDeletedSwitchgearSuccess() {
    setSnackbarSeverity("success");
    setSnackbarText(t("asset.assetDeleted"));
    setShowSnackbar(true);
    setActiveTab(0);

    switchgearsBySubstation.refetch();
  }

  function onDeleteSwitchgearError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.delete"),
          item: t("asset.asset"),
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } else if (errorResponse.status === 409) {
      errorResponse.json().then((error: HvSubstationErrorResponse) => {
        if (
          error.errorCode === HVSubstationSetupErrorCode.PanelIdExistsOnActiveWorkflowException ||
          error.errorCode === HVSubstationSetupErrorCode.AssetExistsOnActiveWorkflowException
        ) {
          setSnackbarText(t("asset.unableToDeleteAssetError"));
          setAffectedWorkflowIds(error.affectedIds);
        } else if (error.errorCode === HVSubstationSetupErrorCode.AssetIdDoesNotBelongToAPanelException) {
          setSnackbarText(t("asset.assetNotFoundError"));
        }
      });
    } else {
      setSnackbarText(t("asset.assetDeleteGenericError"));
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  }

  function onUpdateSwitchgearSuccess() {
    setSnackbarSeverity("success");
    setSnackbarText(t("asset.assetUpdated"));
    setShowSnackbar(true);
    setEditing(false);

    switchgearsBySubstation.refetch();
  }

  function onUpdateSwitchgearError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.update"),
          item: t("asset.asset"),
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } else {
      setSnackbarText(t("common.dataUpdateError", { val: t("asset.asset") }));
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  }

  const handleErrorSnackbarClose = () => {
    setShowSnackbar(false);
  };

  function addSwitchgear(newSwitchgearName: string, newAssetType: string) {
    const surveyableAssetSubmission: SurveyableAssetSubmission = {
      hvSubstationId: substationId,
      surveyableAsset: [
        {
          name: newSwitchgearName,
          assetType: newAssetType,
          panels: [],
        },
      ],
    };
    switchgearAdd.mutate(surveyableAssetSubmission);
  }

  function deleteSwitchgear(switchgearAssetId: number) {
    switchgearDelete.mutate(switchgearAssetId);
  }

  function handleAddSwitchgearClicked() {
    setSwitchgearDialogVisible(true);
  }

  function handleConfirmDeleteSwitchgearClick(switchgearAssetId: number) {
    deleteSwitchgear(switchgearAssetId);
  }

  function handleSwitchgearChange(updated: SurveyableAsset | ResultSurveyableAsset) {
    if (!updated || !("id" in updated)) return;
    if (updated.id == null) return;

    const { id, ...rest }: SurveyableAsset = updated;
    setSwitchgears((prevState) => prevState.map((existing) => (existing.id === id ? { ...rest, id } : existing)));
  }

  function handleSaveChanges() {
    switchgearUpdate.mutate(switchgears[activeTab]);
  }

  function handleCancelChanges() {
    setEditing(false);
    switchgearsBySubstation.refetch();
  }

  if (switchgearAdd.isLoading || switchgearsBySubstation.isLoading) {
    return (
      <Box>
        {[...Array(10).keys()].map((i) => (
          <Skeleton key={`skeleton-row-${i}`} variant="rectangular" sx={{ my: 2 }} />
        ))}
      </Box>
    );
  } else if (switchgearsBySubstation.isError) {
    return (
      <MessageCard
        messageType={MessageType.Error}
        title={t("common.unableToLoadError", { val: "asset.assets" })}
        secondaryText={t("common.dataFetchError", {
          val: "asset.assets",
        })}
      />
    );
  } else
    return (
      <>
        {switchgears.length === 0 ? (
          <MessageCard
            messageType={MessageType.Info}
            title={t("asset.assetsEmptyShort")}
            secondaryText={t("asset.assetsEmpty")}
            cardActions={
              <Button variant="contained" onClick={handleAddSwitchgearClicked}>
                {t("common.addEntity", { entity: "$t(asset.asset)" })}
              </Button>
            }
          />
        ) : (
          <TabView
            activeTabIndex={activeTab}
            onTabChange={handleTabChange}
            tabLabels={switchgears.map((switchgear, index) => (
              <SwitchgearLabel
                switchgear={switchgear}
                editing={editing && activeTab === switchgears.indexOf(switchgear)}
                onChange={handleSwitchgearChange}
                isSelected={activeTab === index}
              />
            ))}
            addButtonEnabed={editable}
            disableInactiveTabs={editing}
            onAddButtonClicked={handleAddSwitchgearClicked}
          >
            {switchgears.map((switchgear) => {
              return (
                <Stack key={`tabContainer-${switchgear.id}`}>
                  {switchgearDelete.isError && affectedWorkflowIds && (
                    <MessageCard
                      title={t("asset.unableToDeleteAssetError")}
                      content={t("asset.assetInUseError", {
                        val: t("workflows.activeWorkflow", {
                          count: affectedWorkflowIds.length,
                        }),
                      })}
                      secondaryText={t("asset.assetRemoveReferenceError", {
                        val: t("workflows.workflow", {
                          count: affectedWorkflowIds.length,
                        }),
                      })}
                      cardActions={
                        <Box display="flex" flexDirection="column">
                          {affectedWorkflowIds.map((workflowId) => (
                            <Link
                              key={`link-${workflowId}`}
                              to={workflowViewerPath?.replace(":workflowId", workflowId.toString() || "") || ""}
                            >
                              {workflowId}
                            </Link>
                          ))}
                        </Box>
                      }
                      messageType={MessageType.Error}
                    />
                  )}
                  <Box display="flex" justifyContent="flex-end" mt={1} mb={2}>
                    <ButtonGroup variant="outlined">
                      {!editing && userCanEditSwitchgears && (
                        <Button color="primary" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                          {t("common.edit")}
                        </Button>
                      )}
                      {!editing && userCanDeleteSwitchgears && (
                        <DeleteConfirmationButton
                          confirmationDialogHeader={t("common.deleteConfirmationShort", {
                            val: `assetTypes.${getAssetTypeLabel(switchgears[activeTab].assetType)}`,
                          })}
                          confirmationDialogMessage={t("common.deleteConfirmation", {
                            val: `assetTypes.${getAssetTypeLabel(switchgears[activeTab].assetType)}`,
                          })}
                          onConfirmDeleteButtonClick={() => handleConfirmDeleteSwitchgearClick(switchgear.id)}
                        >
                          {t("common.deleteEntity", {
                            entity: t(`assetTypes.${getAssetTypeLabel(switchgears[activeTab].assetType)}`),
                          })}
                        </DeleteConfirmationButton>
                      )}

                      {editing && userCanEditSwitchgears && (
                        <>
                          <ProgressButton
                            startIcon={<SaveIcon />}
                            onClick={handleSaveChanges}
                            disabled={switchgearUpdate.isLoading}
                            loading={switchgearUpdate.isLoading}
                          >
                            {t("common.save")}
                          </ProgressButton>
                          <CancelButton onClick={handleCancelChanges} className="m1-1" />
                        </>
                      )}
                    </ButtonGroup>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {getAssetTypeLabel(switchgear.assetType) !== managedSurveyAssetType.Transformer && (
                    <UpdatablePanelDetails
                      editable={editable}
                      switchgearAsset={switchgear}
                      customerId={props.customerId}
                      editingStyle="py-4"
                    />
                  )}

                  <Card title={t("asset.asset")} className="mb-3">
                    {editing ? (
                      <SwitchgearForm switchGear={switchgear} onChange={handleSwitchgearChange} />
                    ) : (
                      <SwitchgearMetadata switchgear={switchgear} />
                    )}
                  </Card>
                </Stack>
              );
            })}
          </TabView>
        )}

        {switchgearDialogVisible && editable && (
          <SwitchgearCreateDialog
            visible={true}
            headerMessage={t("common.addEntity", { entity: "$t(asset.asset)" })}
            placeholder={t("common.name", { entity: "$t(asset.asset)" })}
            maxTextLength={50}
            onConfirm={addSwitchgear}
            onClose={() => setSwitchgearDialogVisible(false)}
            existingSubstationSwitchgearNames={switchgearsBySubstation.data?.map((switchgear) => switchgear.name)}
          />
        )}

        <Snackbar open={showSnackbar} onClose={handleErrorSnackbarClose}>
          <Alert severity={snackbarSeverity} variant="filled">
            {snackbarText}
          </Alert>
        </Snackbar>
      </>
    );
}
